/**
 * When the Weekly Quests section is allowed to show verdicts.
 *
 * Pulled out of GameweekGrid.tsx because it is two set-comprehensions over the grid's reveal
 * bookkeeping, and the thing worth testing about it — that a quest settled at half-time does not
 * wait for the round's last fixture, and that the game cards still land first — is invisible from
 * the rendered markup (the stagger runs in an effect, and the grid's tests render statically).
 */

export interface QuestRevealInputs {
  gameIds: string[]
  /** Whether the game has a result at all: the data half of settledness. */
  hasResult: (gameId: string) => boolean
  /** Whether the user is allowed to see that result yet (the reveal gate proper). */
  isRevealed: (gameId: string) => boolean
  /**
   * The ids whose reveal animation the user has already seen. `null`/`undefined` is "still
   * loading", which is not the same as "none" and opens no gate.
   */
  seenGameIds: ReadonlySet<string> | null | undefined
  /** Whether the card's reveal cascade has finished playing (no entry left in the conductor). */
  cascadeDone: (gameId: string) => boolean
}

export interface QuestRevealGates {
  /** The whole round is settled, revealed and done animating — what a round-scoped quest waits on. */
  questsRevealed: boolean
  /**
   * The same, asked only of the fixtures that have a result **so far**.
   *
   * A half-time quest is graded while the rest of the gameweek is still being played, so gating its
   * card on the last whistle of the round would put the verdict back where the ticket found it:
   * shown for a three-hour window, days after it was decided. The cascade condition is kept, so the
   * ordering still holds — the game cards that can reveal land first, then the quest that reads
   * them — and it stays closed while nothing has settled at all, so an untouched gameweek does not
   * burn its stagger before there is anything to stagger.
   */
  questsEarlyRevealed: boolean
}

export function questRevealGates({
  gameIds,
  hasResult,
  isRevealed,
  seenGameIds,
  cascadeDone,
}: QuestRevealInputs): QuestRevealGates {
  // Loose null check, not `!== null`: some call sites leave the prop off entirely rather than
  // passing the explicit `null` the still-loading state uses.
  if (seenGameIds == null) return { questsRevealed: false, questsEarlyRevealed: false }

  const seen = seenGameIds
  const landed = (id: string) =>
    hasResult(id) && isRevealed(id) && seen.has(id) && cascadeDone(id)
  const settled = gameIds.filter(hasResult)
  return {
    questsRevealed: gameIds.length > 0 && gameIds.every(landed),
    questsEarlyRevealed: settled.length > 0 && settled.every(landed),
  }
}
